import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { 
  Camera, 
  Edit, 
  MapPin, 
  Link as LinkIcon, 
  Twitter, 
  Instagram, 
  Globe,
  Users,
  Eye,
  TrendingUp,
  DollarSign,
  Grid,
  BookOpen,
  Palette,
  UserPlus,
  Settings,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  BadgeCheck,
  Star,
  Award,
  Image as ImageIcon,
  SlidersHorizontal,
  Trash2,
  X as XIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PostComposer from '../components/PostComposer';
import PostCard from '../components/PostCard';
import CoverImage from '../components/CoverImage';
import GalleryArtworkCard from '../components/GalleryArtworkCard';
import ProfileThemeCustomizer from '../components/ProfileThemeCustomizer';
import './CreatorProfile.css';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/${url}`;
};

/**
 * CREATOR PROFILE PAGE - SACRED GROUND FOR CREATOR IP
 * 
 * CRITICAL RULES:
 * 1. ONLY content created by THIS creator appears in portfolio sections
 * 2. ALL content must satisfy: content.creatorId === profile.userId
 * 3. NO foreign IP, NO featured content, NO algorithmic recommendations
 * 4. Creator metrics visible ONLY on own profile
 * 5. Social features (posts) are separate from IP sections
 * 
 * SECTIONS:
 * - Banner & Identity (public)
 * - Creator Metrics (own profile only)
 * - My Comics & Novels (creator's IP only)
 * - Artwork Gallery (creator's IP only)
 * - Social Feed (posts from this creator)
 * - Following (users this creator follows)
 * 
 * This page represents the creator's brand and intellectual property.
 * Treat it as sacred ground.
 */
const CreatorProfile = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // Profile data
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [following, setFollowing] = useState(false);
  const [messagingCreator, setMessagingCreator] = useState(false);

  // Banner & Profile customization
  const [bannerUrl, setBannerUrl] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [editingBanner, setEditingBanner] = useState(false);
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null); // { file, localUrl, uploadedUrl }
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({
    followers: 0,
    following: 0,
    totalViews: 0,
    todayViews: 0,
    newFollowersToday: 0,
    topComic: null
  });

  // Content
  const [comics, setComics] = useState([]);
  const [novels, setNovels] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  
  // Badges
  const [badges, setBadges] = useState([]);

  // Featured carousel
  const [featuredItems, setFeaturedItems] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Background wallpaper
  const [bgUrl, setBgUrl] = useState('');
  const [bgBlur, setBgBlur] = useState(false);
  const [bgDim, setBgDim] = useState(60);
  const [bgPosition, setBgPosition] = useState('center');
  const [bgLoaded, setBgLoaded] = useState(false);
  const [showBgSettings, setShowBgSettings] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const bgImgRef = useRef(null);
  const [bgTab, setBgTab] = useState('upload'); // 'upload' | 'presets'
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [handle, user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Try creator profile first
      let response;
      try {
        response = await axios.get(`${API}/creator/profile/${handle}`);
      } catch (err) {
        // If not found, try reader profile
        if (err.response?.status === 404) {
          response = await axios.get(`${API}/reader/profile/${handle}`);
        } else {
          throw err;
        }
      }
      
      const profileData = response.data;
      
      // Handle nested structure
      const actualProfile = profileData.profile || profileData;
      const userData = profileData.user || {};
      
      setProfile({
        ...actualProfile,
        user_id: userData.id || actualProfile.user_id,
        handle: actualProfile.handle || handle,
        display_name: actualProfile.display_name || userData.email?.split('@')[0] || handle,
        bio: actualProfile.bio || '',
        avatar_url: actualProfile.avatar_url || userData.avatar_url,
        banner_url: actualProfile.cover_image_url || actualProfile.banner_url,
      });
      
      setBannerUrl(actualProfile.cover_image_url || actualProfile.banner_url || '');
      setProfilePicUrl(actualProfile.avatar_url || userData.avatar_url || '');
      
      // Populate background wallpaper settings
      setBgUrl(actualProfile.profile_background_url || '');
      setBgBlur(actualProfile.profile_background_blur || false);
      setBgDim(typeof actualProfile.profile_background_dim === 'number' ? actualProfile.profile_background_dim : 60);
      setBgPosition(actualProfile.profile_background_position || 'center');
      setBgLoaded(false);
      
      // Check if own profile - AGGRESSIVE CHECK
      const userId = userData.id || actualProfile.user_id;
      
      // AGGRESSIVE: Check localStorage for user data to help determine ownership
      let localUser = null;
      try {
        const localUserStr = localStorage.getItem('user');
        if (localUserStr) {
          localUser = JSON.parse(localUserStr);
        }
      } catch (e) {
        // Ignore JSON/localStorage errors
      }
      
      // AGGRESSIVE: Multiple checks to determine ownership
      const own = !!(
        // Standard checks
        (user && user.id === userId) ||
        (user && user.handle === handle) ||
        (user && user.email === userData.email) ||
        (user && user.email === actualProfile.email) ||
        (user && actualProfile.user_id && user.id === actualProfile.user_id) ||
        
        // LocalStorage checks (more reliable than auth context sometimes)
        (localUser && localUser.id === userId) ||
        (localUser && localUser.handle === handle) ||
        (localUser && localUser.email === userData.email) ||
        (localUser && localUser.email === actualProfile.email) ||
        
        // Token-based check - if we have a token and the API returned this profile, likely ours
        (localStorage.getItem('token') && actualProfile.user_id && user?.id === actualProfile.user_id) ||
        
        // Last resort: if the profile email matches the logged-in email from ANY source
        (user?.email && (userData.email === user.email || actualProfile.email === user.email)) ||
        (localUser?.email && (userData.email === localUser.email || actualProfile.email === localUser.email))
      );
      
      setIsOwnProfile(own);

      // BACKUP CHECK: If ownership is false but we have a token, verify via API
      if (!own && localStorage.getItem('token')) {
        try {
          const meResponse = await axios.get(`${API}/creators/me/profile`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          const myProfile = meResponse.data.profile || meResponse.data;
          const myHandle = myProfile.handle;
          
          if (myHandle === handle) {
            setIsOwnProfile(true);
          }
        } catch (error) {
          // Ignore backup check errors
        }
      }

      // Fetch stats
      if (userId) {
        fetchStats(userId);
        // Use ownership flag to correctly handle visibility of drafts/unlisted items
        fetchComics(userId, own);
        fetchNovels(userId, own);
        fetchArtworks(userId);
        fetchFollowing(userId);
        fetchPosts(userId);
        fetchBadges(userId);
      }

    } catch (error) {
      console.error('Error fetching profile:', error);
      
      // If logged-in user viewing their own profile that doesn't exist, redirect to onboarding
      if (user && (user.handle === handle || handle === 'me')) {
        toast.info('Complete your profile setup');
        navigate('/profile');
        return;
      }
      
      // Otherwise show error
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchComics = async (userId, isOwner = false) => {
    try {
      const response = await axios.get(`${API}/series?creator_id=${userId}`);
      const data = response.data;
      
      // Normalize to array: handle array, object with array property, or fallback to empty
      let comicsArray = Array.isArray(data) ? data : 
                       Array.isArray(data?.items) ? data.items :
                       Array.isArray(data?.series) ? data.series :
                       Array.isArray(data?.comics) ? data.comics : [];
      
      // CRITICAL: Filter to ensure ONLY this creator's content
      const filteredComics = comicsArray.filter((comic) => {
        const creatorMatch =
          comic.creator_id === userId ||
          comic.creatorId === userId ||
          comic.user_id === userId;

        if (!creatorMatch) return false;

        // Visibility rule: drafts/unlisted only visible to owner
        const status = comic.status || comic.series_status;
        const isDraftLike =
          status === 'draft' ||
          status === 'unlisted' ||
          status === 'private';

        if (!isOwner && isDraftLike) {
          return false;
        }

        return true;
      });

      setComics(filteredComics);
    } catch (error) {
      console.error('[fetchComics] Error:', error);
      setComics([]); // Always set to empty array on error
    }
  };

  const fetchNovels = async (userId, isOwner = false) => {
    try {
      const response = await axios.get(`${API}/novels?creator_id=${userId}`);
      const data = response.data;
      // Normalize to array
      let novelsArray = Array.isArray(data) ? data :
                       Array.isArray(data?.novels) ? data.novels : [];
      
      // CRITICAL: Filter to ensure ONLY this creator's content
      novelsArray = novelsArray.filter((novel) => {
        const creatorMatch =
          novel.creator_id === userId ||
          novel.creatorId === userId ||
          novel.user_id === userId;

        if (!creatorMatch) return false;

        const status = novel.status;
        const isDraftLike =
          status === 'draft' ||
          status === 'unlisted' ||
          status === 'private';

        if (!isOwner && isDraftLike) {
          return false;
        }

        return true;
      });
      
      setNovels(novelsArray);
    } catch (error) {
      console.error('Error fetching novels:', error);
      setNovels([]); // Always set to empty array on error
    }
  };

  const fetchArtworks = async (userId) => {
    try {
      const response = await axios.get(`${API}/art?creator_id=${userId}`);
      const data = response.data;
      // Normalize to array
      let artworksArray = Array.isArray(data) ? data :
                         Array.isArray(data?.artworks) ? data.artworks :
                         Array.isArray(data?.art) ? data.art : [];
      
      // CRITICAL: Filter to ensure ONLY this creator's content
      artworksArray = artworksArray.filter(artwork => 
        artwork.creator_id === userId || 
        artwork.creatorId === userId ||
        artwork.user_id === userId
      );
      
      setArtworks(artworksArray);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      setArtworks([]); // Always set to empty array on error
    }
  };

  const fetchFollowing = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/following`);
      setFollowingList(response.data || []);
    } catch (error) {
      console.error('Error fetching following:', error);
    }
  };

  const fetchPosts = async (userId) => {
    try {
      setLoadingPosts(true);
      const response = await axios.get(`${API}/creator/${userId}/posts`);
      const data = response.data;
      const postsArray = Array.isArray(data) ? data :
                        Array.isArray(data?.posts) ? data.posts : [];
      setPosts(postsArray);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handlePostCreated = (newPost) => {
    // Add new post to top of feed
    setPosts(prevPosts => [newPost, ...prevPosts]);
    toast.success('Post created!');
  };

  const handlePostUpdate = (updatedPost) => {
    setPosts(prevPosts => 
      prevPosts.map(post => post.id === updatedPost.id ? updatedPost : post)
    );
  };

  const handlePostDelete = (deletedPostId) => {
    setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));
    toast.success('Post deleted');
  };

  // ── Banner upload modal handlers ──
  const openBannerModal = () => {
    setBannerPreview(null);
    setEditingBanner(true);
  };

  const closeBannerModal = () => {
    // Revoke object URL to prevent memory leaks
    if (bannerPreview?.localUrl) URL.revokeObjectURL(bannerPreview.localUrl);
    setBannerPreview(null);
    setEditingBanner(false);
  };

  const handleBannerFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Banner image must be under 5 MB');
      return;
    }

    const localUrl = URL.createObjectURL(file);
    setBannerPreview({ file, localUrl, uploadedUrl: null });

    // Upload immediately so Save just persists the URL
    setBannerUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/banner`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url || res.data.image_url;
      setBannerPreview(prev => prev ? { ...prev, uploadedUrl: url } : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Banner upload failed');
      setBannerPreview(null);
    } finally {
      setBannerUploading(false);
    }
  };

  const saveBanner = async () => {
    const url = bannerPreview?.uploadedUrl;
    if (!url) return;

    setBannerSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/creator/profile/banner`, { banner_url: url }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBannerUrl(url);
      toast.success('Banner updated!');
      closeBannerModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banner');
    } finally {
      setBannerSaving(false);
    }
  };

  const removeBanner = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/creator/profile/banner`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBannerUrl('');
      toast.success('Banner removed');
      closeBannerModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove banner');
    }
  };

  // ── Profile picture upload handler ──
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, and WebP images are supported'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2 MB'); return; }

    setAvatarUploading(true);
    try {
      const token = localStorage.getItem('token');
      // Upload file
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = uploadRes.data.url;
      // Persist to profile
      await axios.put(`${API}/creator/profile/picture`, { profile_image_url: url }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfilePicUrl(url);
      updateUser({ ...user, profile_image_url: url });
      toast.success('Profile picture updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const removeAvatar = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/creator/profile/picture`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfilePicUrl('');
      updateUser({ ...user, profile_image_url: null });
      toast.success('Profile picture removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove profile picture');
    }
  };

  // ── Background wallpaper handlers ──
  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are supported');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10 MB');
      return;
    }

    setUploadingBg(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/profile-background`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.url;
      setBgUrl(url);
      setBgLoaded(false);
      toast.success('Background uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingBg(false);
    }
  };

  const saveBgSettings = async (overrides = {}) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        profile_background_url: overrides.profile_background_url ?? bgUrl,
        profile_background_blur: overrides.profile_background_blur ?? bgBlur,
        profile_background_dim: overrides.profile_background_dim ?? bgDim,
        profile_background_position: overrides.profile_background_position ?? bgPosition,
      };
      await axios.put(`${API}/creator/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Background settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const removeBg = async () => {
    setBgUrl('');
    setBgLoaded(false);
    setSelectedPresetId(null);
    await saveBgSettings({ profile_background_url: '' });
  };

  // Fetch presets when panel opens on the presets tab
  useEffect(() => {
    if (showBgSettings && presets.length === 0 && !presetsLoading) {
      setPresetsLoading(true);
      axios.get(`${API}/profile-background-presets`)
        .then(res => setPresets(res.data.presets || []))
        .catch(() => toast.error('Failed to load presets'))
        .finally(() => setPresetsLoading(false));
    }
  }, [showBgSettings]);

  const selectPreset = (preset) => {
    setSelectedPresetId(preset.id);
    // Immediately update local state for live preview
    setBgUrl(preset.image_url);
    setBgBlur(preset.default_blur);
    setBgDim(preset.default_dim);
    setBgPosition(preset.default_position);
    setBgLoaded(false);
  };

  const applyPreset = async () => {
    const preset = presets.find(p => p.id === selectedPresetId);
    if (!preset) return;
    await saveBgSettings({
      profile_background_url: preset.image_url,
      profile_background_blur: preset.default_blur,
      profile_background_dim: preset.default_dim,
      profile_background_position: preset.default_position,
    });
    setShowBgSettings(false);
  };

  const handleFollowToggle = async () => {
    if (!user) {
      toast.error('Please sign in to follow');
      return;
    }

    try {
      if (following) {
        await axios.delete(`${API}/users/${profile.user_id}/follow`);
        setFollowing(false);
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/users/${profile.user_id}/follow`);
        setFollowing(true);
        toast.success('Following!');
      }
      fetchStats(profile.user_id);
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const viewerIsCreator = user && (user.is_creator === true || user.role === 'creator' || user.role === 'artist' || user.role === 'author');

  const handleMessageCreator = async () => {
    if (!user) { toast.error('Please sign in to message'); return; }
    if (!viewerIsCreator) { toast.error('Only creators can message other creators'); return; }
    if (messagingCreator) return;
    setMessagingCreator(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/conversations/with`, 
        { other_user_id: profile.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/messages?conversation=${res.data.conversation_id}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start conversation';
      toast.error(msg);
    } finally {
      setMessagingCreator(false);
    }
  };

  const fetchBadges = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/badges`);
      setBadges(response.data.badges || []);
    } catch {
      // Silently fail - badges are optional
      setBadges([]);
    }
  };

  // Badge helper functions
  const getBadgeIcon = (badgeKey) => {
    switch (badgeKey) {
      case 'human_verified': return <BadgeCheck className="w-4 h-4" />;
      case 'early_creator': return <Star className="w-4 h-4" />;
      case 'featured_creator': return <Award className="w-4 h-4" />;
      default: return <Award className="w-4 h-4" />;
    }
  };

  const getBadgeColor = (color) => {
    switch (color) {
      case 'green': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'yellow': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'purple': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Profile not found</div>
      </div>
    );
  }

  return (
    <div className="creatorProfilePage" data-testid="creator-profile-page">
      {/* ── Fixed wallpaper layer ── */}
      {bgUrl ? (
        <div
          className="creatorProfileBg"
          data-testid="creator-profile-bg"
          style={{
            backgroundImage: bgLoaded ? `url(${bgUrl})` : 'none',
            backgroundPosition: bgPosition || 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            ...(bgBlur ? { filter: 'blur(8px)', transform: 'scale(1.05) translateZ(0)' } : { transform: 'translateZ(0)' }),
          }}
        />
      ) : (
        <div className="creatorProfileBg creatorProfileBg--default" data-testid="creator-profile-bg-default" />
      )}

      {/* Lazy-load the actual image without displaying it */}
      {bgUrl && !bgLoaded && (
        <img
          ref={bgImgRef}
          src={bgUrl}
          alt=""
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          onLoad={() => setBgLoaded(true)}
        />
      )}

      {/* ── Dim scrim overlay ── */}
      <div
        className="creatorProfileScrim"
        data-testid="creator-profile-scrim"
        style={{ backgroundColor: `rgba(0,0,0,${(bgUrl ? bgDim : 0) / 100})` }}
      />

      {/* ── Scrolling content ── */}
      <div className="creatorProfileContent" data-testid="creator-profile-content">
      {/* Banner Section */}
      <div className="relative w-full h-52 sm:h-64 md:h-80 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden" data-testid="banner-section">
        {bannerUrl && (
          <img 
            src={bannerUrl} 
            alt="Profile Banner" 
            className="w-full h-full object-cover"
            data-testid="banner-image"
          />
        )}
        
        {/* Customize Banner Button */}
        {isOwnProfile && (
          <button
            onClick={openBannerModal}
            className="absolute top-4 right-4 px-4 py-2 bg-gray-900/80 hover:bg-gray-800 rounded-lg cursor-pointer transition-colors flex items-center gap-2"
            data-testid="customize-banner-btn"
          >
            <Camera className="w-4 h-4" />
            <span className="text-sm">Customize Banner</span>
          </button>
        )}

        {/* Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 to-transparent" />
      </div>

      {/* Profile Info Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-20 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-40 h-40 rounded-full border-4 border-black bg-gray-800 overflow-hidden">
                {profilePicUrl ? (
                  <img 
                    src={profilePicUrl} 
                    alt={profile.display_name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                    <span className="text-white text-5xl font-bold">
                      {(profile.display_name || profile.handle || 'U')[0].toUpperCase()}
                    </span>
                  </div>
                )}
                {avatarUploading && (
                  <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              {isOwnProfile && (
                <>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={avatarUploading}
                    className="absolute bottom-2 right-2 w-10 h-10 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 rounded-full flex items-center justify-center transition-colors"
                    data-testid="avatar-upload-btn"
                    title="Change profile picture"
                  >
                    <Camera className="w-5 h-5 text-white" />
                  </button>
                  {profilePicUrl && (
                    <button
                      onClick={removeAvatar}
                      className="absolute top-0 right-0 w-7 h-7 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors text-white"
                      data-testid="avatar-remove-btn"
                      title="Remove profile picture"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Name, Stats, Bio */}
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-4xl font-bold mb-1">{profile.display_name || profile.handle}</h1>
                  <p className="text-gray-400 text-lg">@{profile.handle}</p>
                  
                  {/* Badges */}
                  {badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {badges.map(badge => (
                        <div 
                          key={badge.key} 
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm ${getBadgeColor(badge.color)}`}
                          title={badge.label}
                        >
                          {getBadgeIcon(badge.key)}
                          <span>{badge.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Stats Row */}
                  <div className="flex items-center gap-6 mt-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-gray-500" />
                      <span className="font-semibold">{stats.followers.toLocaleString()}</span>
                      <span className="text-gray-500">Followers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{stats.following.toLocaleString()}</span>
                      <span className="text-gray-500">Following</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-gray-500" />
                      <span className="font-semibold">{stats.totalViews.toLocaleString()}</span>
                      <span className="text-gray-500">Views</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 flex-wrap">
                  {isOwnProfile ? (
                    <>
                      <Button
                        onClick={() => navigate('/settings/profile')}
                        variant="outline"
                        className="bg-gray-800/80 hover:bg-gray-700 border-gray-700"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                      <Button
                        onClick={() => setShowBgSettings(true)}
                        variant="outline"
                        className="bg-gray-800/80 hover:bg-gray-700 border-purple-500 text-purple-400 hover:text-purple-300"
                        data-testid="background-settings-btn"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Background
                      </Button>
                      <Button
                        onClick={() => setShowThemeCustomizer(true)}
                        variant="outline"
                        className="bg-gray-800/80 hover:bg-gray-700 border-orange-500 text-orange-400 hover:text-orange-300"
                        data-testid="customize-theme-btn"
                      >
                        <Palette className="w-4 h-4 mr-2" />
                        Theme
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={handleFollowToggle}
                        className={following 
                          ? "bg-gray-700 hover:bg-gray-600" 
                          : "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                        }
                        data-testid="follow-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        {following ? 'Following' : 'Follow'}
                      </Button>
                      {viewerIsCreator && (
                        <Button
                          onClick={handleMessageCreator}
                          disabled={messagingCreator}
                          variant="outline"
                          className="bg-gray-800 hover:bg-gray-700 border-gray-700"
                          data-testid="message-creator-btn"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          {messagingCreator ? 'Opening...' : 'Message'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="bg-gray-800 hover:bg-gray-700 border-gray-700"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Bio / Tagline */}
              <div className="mb-4">
                <p className="text-gray-300 max-w-2xl">
                  {profile.bio || 'No bio yet.'}
                </p>
              </div>

              {/* Social Links */}
              {profile.social_links && (
                <div className="flex items-center gap-4">
                  {profile.social_links.twitter && (
                    <a href={profile.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {profile.social_links.instagram && (
                    <a href={profile.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {profile.social_links.website && (
                    <a href={profile.social_links.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats Bar - OWN PROFILE ONLY */}
        {isOwnProfile && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Today&apos;s Views</span>
              <TrendingUp className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{stats.todayViews.toLocaleString()}</p>
          </div>

          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Total Views</span>
              <Eye className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{stats.totalViews.toLocaleString()}</p>
          </div>

          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">New Followers&nbsp;Today</span>
              <Users className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold">{stats.newFollowersToday.toLocaleString()}</p>
          </div>

          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Top Comic</span>
              <BookOpen className="w-4 h-4 text-orange-500" />
            </div>
            <p className="text-sm font-semibold truncate">{stats.topComic || 'N/A'}</p>
          </div>

          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4 opacity-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Earnings</span>
              <DollarSign className="w-4 h-4 text-gray-500" />
            </div>
            <p className="text-sm text-gray-500">Coming Soon</p>
          </div>
        </div>
        )}

        {/* Two-Column Layout (Desktop) + Mobile Stack */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-12">
          {/* MOBILE ONLY: Social Section First */}
          <div className="lg:hidden space-y-6 order-1">
            {/* Create Post Composer (Own Profile Only) */}
            {isOwnProfile && (
              <PostComposer 
                onPostCreated={handlePostCreated}
                collapsed={false}
              />
            )}

            {/* Posts Feed */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <h2 className="text-xl font-bold mb-6">Recent Posts</h2>
              
              {loadingPosts ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-gray-700 border-t-orange-500 rounded-full animate-spin"></div>
                  <p className="text-gray-400 mt-4">Loading posts...</p>
                </div>
              ) : (Array.isArray(posts) ? posts : []).length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400">No posts yet</p>
                  {isOwnProfile && (
                    <p className="text-gray-500 text-sm mt-2">
                      Share your thoughts with your followers
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {(Array.isArray(posts) ? posts : []).map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      profileHandle={profile?.handle}
                      onUpdate={handlePostUpdate}
                      onDelete={handlePostDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* LEFT COLUMN - CREATOR IP ONLY (3/5 width on desktop, order-2 on mobile) */}
          <div className="lg:col-span-3 space-y-4 order-2">
            
            {/* ═══════════════════════════════════════════════════════════
                CREATOR IP ONLY - My Comics
                CRITICAL: Only content where creatorId === profile.userId
                ═══════════════════════════════════════════════════════ */}
            <div data-testid="my-comics-section" className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Grid className="w-6 h-6 text-orange-500" />
                  My Comics
                </h2>
                {isOwnProfile && (
                  <Button 
                    data-testid="add-comic-btn"
                    onClick={() => navigate('/create-series')}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                  >
                    + Add Comic
                  </Button>
                )}
              </div>

              {(Array.isArray(comics) ? comics : []).length === 0 ? (
                <div data-testid="comics-empty-state" className="text-center py-12">
                  <Grid className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400">No comics yet</p>
                  {isOwnProfile && (
                    <Button 
                      onClick={() => navigate('/create-series')}
                      variant="outline"
                      className="mt-4 border-gray-700"
                    >
                      Create Your First Comic
                    </Button>
                  )}
                </div>
              ) : (
                <div data-testid="comics-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(Array.isArray(comics) ? comics : []).map((series) => (
                    <Link
                      key={series.id}
                      to={`/series/${series.id}`}
                      className="group"
                      data-testid={`comic-card-${series.id}`}
                    >
                      <CoverImage 
                        item={series}
                        type="comic"
                        alt={series.title}
                        className="mb-2 group-hover:scale-[1.02] transition-transform"
                      />
                      <h3 data-testid={`comic-title-${series.id}`} className="font-semibold text-sm truncate group-hover:text-orange-500 transition-colors">
                        {series.title}
                      </h3>
                      <p className="text-xs text-gray-500">{series.genre || series.genres?.[0] || ''}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════
                CREATOR IP ONLY - My Novels
                CRITICAL: Only content where creatorId === profile.userId
                ═══════════════════════════════════════════════════════ */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-green-500" />
                  My Novels
                </h2>
                {isOwnProfile && (
                  <Button 
                    onClick={() => navigate('/create-novel')}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  >
                    + Add Novel
                  </Button>
                )}
              </div>

              {(Array.isArray(novels) ? novels : []).length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400">No novels yet</p>
                  {isOwnProfile && (
                    <Button 
                      onClick={() => navigate('/create-novel')}
                      variant="outline"
                      className="mt-4 border-gray-700"
                    >
                      Create Your First Novel
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(Array.isArray(novels) ? novels : []).map((novel) => (
                    <Link
                      key={novel.id}
                      to={`/novels/${novel.id}`}
                      className="group"
                    >
                      <CoverImage 
                        item={novel}
                        type="novel"
                        alt={novel.title}
                        className="mb-2 group-hover:scale-[1.02] transition-transform"
                      />
                      <h3 className="font-semibold text-sm truncate group-hover:text-green-500 transition-colors">
                        {novel.title}
                      </h3>
                      <p className="text-xs text-gray-500">{novel.genres?.[0] || ''}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════
                CREATOR IP ONLY - Artwork Gallery
                CRITICAL: Only artwork where creatorId === profile.userId
                ═══════════════════════════════════════════════════════ */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Palette className="w-6 h-6 text-purple-500" />
                  Artwork Gallery
                </h2>
                {isOwnProfile && (
                  <Button 
                    onClick={() => navigate('/upload-artwork')}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    Upload Artwork
                  </Button>
                )}
              </div>

              {(Array.isArray(artworks) ? artworks : []).length === 0 ? (
                <div className="text-center py-12">
                  <Palette className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400">No artwork yet</p>
                  {isOwnProfile && (
                    <Button 
                      onClick={() => navigate('/upload-artwork')}
                      variant="outline"
                      className="mt-4 border-gray-700"
                    >
                      Upload Your First Artwork
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="artwork-gallery-grid">
                  {(Array.isArray(artworks) ? artworks : []).map((artwork) => (
                    <GalleryArtworkCard key={artwork.id} artwork={artwork} />
                  ))}
                </div>
              )}
            </div>

            {/* Following Section - LEFT COLUMN */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Following ({(Array.isArray(followingList) ? followingList : []).length})
              </h2>

              {(Array.isArray(followingList) ? followingList : []).length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Not following anyone yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(Array.isArray(followingList) ? followingList : []).slice(0, 5).map((followedUser) => (
                    <Link
                      key={followedUser.id}
                      to={`/u/${followedUser.handle}`}
                      className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <div className="w-10 h-10 bg-gray-800 rounded-full overflow-hidden">
                        {followedUser.avatar_url && (
                          <img 
                            src={followedUser.avatar_url} 
                            alt={followedUser.display_name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{followedUser.display_name}</p>
                        <p className="text-xs text-gray-500 truncate">@{followedUser.handle}</p>
                      </div>
                    </Link>
                  ))}
                  {Array.isArray(followingList) && followingList.length > 5 && (
                    <button className="w-full text-center text-sm text-orange-500 hover:text-orange-400 py-2">
                      View All
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN - SOCIAL & COMMUNITY (2/5 width on desktop, hidden on mobile) */}
          <div className="hidden lg:block space-y-4 lg:col-span-2 order-3">
            
            
            {/* Create Post Composer (Own Profile Only) */}
            {isOwnProfile && (
              <div className="mb-6">
                <PostComposer 
                  onPostCreated={handlePostCreated}
                  collapsed={false}
                />
              </div>
            )}

            {/* Posts Feed */}
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-5">
              <h2 className="text-xl font-bold mb-6">Recent Posts</h2>
              
              {loadingPosts ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-gray-700 border-t-orange-500 rounded-full animate-spin"></div>
                  <p className="text-gray-400 mt-4">Loading posts...</p>
                </div>
              ) : (Array.isArray(posts) ? posts : []).length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400">No posts yet</p>
                  {isOwnProfile && (
                    <p className="text-gray-500 text-sm mt-2">
                      Share your thoughts with your followers
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {(Array.isArray(posts) ? posts : []).map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      profileHandle={profile?.handle}
                      onUpdate={handlePostUpdate}
                      onDelete={handlePostDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Theme Customizer Modal (MySpace-style) */}
      <ProfileThemeCustomizer
        isOpen={showThemeCustomizer}
        onClose={() => setShowThemeCustomizer(false)}
        profileData={profile}
        onThemeUpdate={(updatedTheme) => {
          toast.success('Theme saved! Refresh to see changes on public profile.');
        }}
      />

      </div>{/* end creatorProfileContent */}

      {/* ── Banner Upload Modal ── */}
      {editingBanner && (
        <div className="fixed inset-0 z-[9999]" data-testid="banner-modal-overlay">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeBannerModal} />
          <div className="absolute inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col max-h-[90vh]" data-testid="banner-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-zinc-700 shrink-0">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-orange-400" />
                Customize Banner
              </h2>
              <button onClick={closeBannerModal} className="text-zinc-400 hover:text-white" data-testid="banner-modal-close">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Preview area */}
              <div className="relative w-full aspect-[21/7] bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700" data-testid="banner-preview-area">
                {(bannerPreview?.localUrl || bannerUrl) ? (
                  <img
                    src={bannerPreview?.localUrl || bannerUrl}
                    alt="Banner preview"
                    className="w-full h-full object-cover"
                    data-testid="banner-preview-image"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
                    <span className="text-zinc-500 text-sm">No banner set</span>
                  </div>
                )}

                {/* Upload progress overlay */}
                {bannerUploading && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-testid="banner-uploading">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <span className="text-sm text-zinc-300">Uploading...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* File picker */}
              <label className="flex items-center justify-center gap-2 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded-lg cursor-pointer transition-colors" data-testid="banner-file-picker">
                <Camera className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-zinc-300">{bannerPreview ? 'Choose Different Image' : 'Choose Image'}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleBannerFileSelect}
                  className="sr-only"
                  disabled={bannerUploading || bannerSaving}
                />
              </label>
              <p className="text-xs text-zinc-500">Recommended: 1500×500px or wider. JPG, PNG, WebP. Max 5 MB.</p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-zinc-700 shrink-0">
              <div>
                {bannerUrl && (
                  <button
                    onClick={removeBanner}
                    className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1.5 transition-colors"
                    data-testid="banner-remove-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Banner
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={closeBannerModal} className="border-zinc-600 text-zinc-300" data-testid="banner-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={saveBanner}
                  className="bg-orange-500 hover:bg-orange-600"
                  disabled={!bannerPreview?.uploadedUrl || bannerUploading || bannerSaving}
                  data-testid="banner-save-btn"
                >
                  {bannerSaving ? 'Saving...' : 'Save Banner'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Background Settings Panel (slide-over) ── */}
      {showBgSettings && (
        <div className="fixed inset-0 z-[9999]" data-testid="bg-settings-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowBgSettings(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-zinc-900 border-l border-zinc-700 shadow-2xl flex flex-col overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-zinc-700">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-400" />
                Profile Background
              </h2>
              <button onClick={() => setShowBgSettings(false)} className="text-zinc-400 hover:text-white" data-testid="bg-settings-close">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-700" data-testid="bg-tabs">
              <button
                onClick={() => setBgTab('upload')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${bgTab === 'upload' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid="bg-tab-upload"
              >
                Upload
              </button>
              <button
                onClick={() => setBgTab('presets')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${bgTab === 'presets' ? 'text-purple-400 border-b-2 border-purple-400' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid="bg-tab-presets"
              >
                Presets
              </button>
            </div>

            <div className="p-5 space-y-6 flex-1">

              {/* ── Upload Tab ── */}
              {bgTab === 'upload' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Wallpaper Image</label>
                    {bgUrl ? (
                      <div className="relative rounded-lg overflow-hidden border border-zinc-700 aspect-video">
                        <img src={bgUrl} alt="Background preview" className="w-full h-full object-cover" />
                        <button
                          onClick={removeBg}
                          className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                          data-testid="bg-remove-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-zinc-600 rounded-lg cursor-pointer hover:border-purple-500 transition-colors" data-testid="bg-upload-area">
                        <ImageIcon className="w-8 h-8 text-zinc-500 mb-2" />
                        <span className="text-sm text-zinc-400">{uploadingBg ? 'Uploading...' : 'Click to upload'}</span>
                        <span className="text-xs text-zinc-600 mt-1">JPG, PNG, WebP (max 10 MB)</span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleBgUpload} className="sr-only" disabled={uploadingBg} />
                      </label>
                    )}
                    {bgUrl && (
                      <label className="mt-2 inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 cursor-pointer" data-testid="bg-change-btn">
                        <Camera className="w-4 h-4" />
                        Change image
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleBgUpload} className="sr-only" disabled={uploadingBg} />
                      </label>
                    )}
                  </div>

                  {/* Position */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Position</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['top', 'center', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => setBgPosition(pos)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            bgPosition === pos
                              ? 'bg-purple-500 text-white'
                              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                          }`}
                          data-testid={`bg-position-${pos}`}
                        >
                          {pos.charAt(0).toUpperCase() + pos.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Dim */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Dim Overlay <span className="text-zinc-500 font-normal">({bgDim}%)</span>
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={80}
                      value={bgDim}
                      onChange={(e) => setBgDim(Number(e.target.value))}
                      className="w-full accent-purple-500"
                      data-testid="bg-dim-slider"
                    />
                    <div className="flex justify-between text-xs text-zinc-600 mt-1">
                      <span>None</span>
                      <span>Max</span>
                    </div>
                  </div>

                  {/* Blur toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-zinc-300">Background Blur</label>
                      <p className="text-xs text-zinc-500">Softens the wallpaper image</p>
                    </div>
                    <button
                      onClick={() => setBgBlur(!bgBlur)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${bgBlur ? 'bg-purple-500' : 'bg-zinc-600'}`}
                      data-testid="bg-blur-toggle"
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${bgBlur ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </>
              )}

              {/* ── Presets Tab ── */}
              {bgTab === 'presets' && (
                <div data-testid="bg-presets-panel">
                  {presetsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Group by category */}
                      {(() => {
                        const cats = [...new Set(presets.map(p => p.category))];
                        return cats.map(cat => (
                          <div key={cat} className="mb-5">
                            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">{cat}</h3>
                            <div className="grid grid-cols-2 gap-2" data-testid={`bg-preset-category-${cat.toLowerCase()}`}>
                              {presets.filter(p => p.category === cat).map(preset => (
                                <button
                                  key={preset.id}
                                  onClick={() => selectPreset(preset)}
                                  className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all ${
                                    selectedPresetId === preset.id
                                      ? 'border-purple-500 ring-2 ring-purple-500/40 scale-[1.02]'
                                      : 'border-zinc-700 hover:border-zinc-500'
                                  }`}
                                  data-testid={`bg-preset-${preset.id}`}
                                >
                                  <img
                                    src={preset.thumbnail_url}
                                    alt={preset.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                  <span className="absolute bottom-1.5 left-2 text-[11px] font-medium text-white/90">{preset.name}</span>
                                  {selectedPresetId === preset.id && (
                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-zinc-700">
              {bgTab === 'presets' && selectedPresetId ? (
                <Button
                  onClick={applyPreset}
                  className="w-full bg-purple-500 hover:bg-purple-600"
                  data-testid="bg-apply-preset-btn"
                >
                  Apply Preset
                </Button>
              ) : (
                <Button
                  onClick={() => { saveBgSettings(); setShowBgSettings(false); }}
                  className="w-full bg-purple-500 hover:bg-purple-600"
                  data-testid="bg-save-btn"
                >
                  Save Background
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorProfile;
